import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import Field from 'Components/UI/Field';
import Form from 'Components/UI/Form/Form';
import { ISpeRequest, ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';
import {
  stringToCEP,
  stringToCNPJ,
  stringToCPF,
  stringToPhone,
} from 'Utils/MaskUtils';
import schema from './SpeFormModal.schema';
import { ModalBody } from '../ModalBody';
import { Col, Row, Section, SectionHint, SectionTitle } from './styles';

interface ISpeFormModal {
  visible: boolean;
  // Presente = edição; ausente = cadastro novo.
  spe?: ISpeResponse;
  isSaving?: boolean;
  onSubmit: (spe: ISpeRequest) => void;
  onCancel: () => void;
}

// Converte a SPE vinda do servidor (PascalCase) para o shape do formulário, que é
// o mesmo do request (camelCase). Sem isso o Form abriria em branco na edição.
const toFormValues = (spe?: ISpeResponse): Partial<ISpeRequest> => {
  if (!spe) return {};

  return {
    companyName: spe.CompanyName,
    cnpj: spe.CNPJ,
    phone: spe.Phone,
    email: spe.Email,
    ownerName: spe.OwnerName,
    site: spe.Site,
    zip: spe.Zip,
    streetAddress: spe.StreetAddress,
    number: spe.Number,
    district: spe.District,
    city: spe.City,
    state: spe.State,
    complement: spe.Complement,
    legalRepresentative: {
      name: spe.LegalRepresentative.Name,
      cpf: spe.LegalRepresentative.CPF,
      role: spe.LegalRepresentative.Role,
      email: spe.LegalRepresentative.Email,
      phone: spe.LegalRepresentative.Phone,
    },
  };
};

// O Field usa o label como placeholder quando nenhum é passado. Aqui passamos
// explicitamente com "*" nos obrigatórios; o label flutuante, que aparece quando o
// campo está preenchido, continua sem o asterisco.
const req = (label: string) => `${label} *`;

const SpeFormModal: FC<ISpeFormModal> = ({
  visible,
  spe,
  isSaving,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation(['faturamento', 'common']);
  const isEditing = Boolean(spe);

  // O botão de salvar fica no rodapé do Modal, fora do <form>, então precisamos da
  // API do final-form para submeter de fora. Capturada via children-as-function,
  // que o Form do projeto suporta.
  const formApi = useRef<any>(null);

  const initialValues = useMemo(() => toFormValues(spe), [spe]);

  const submit = (values?: any) => {
    if (!values) return;

    onSubmit({
      ...values,
      number: Number(values.number),
    } as ISpeRequest);
  };

  const onSaveClick = () => {
    const api = formApi.current;
    if (!api) return;

    // O Field só pinta a borda de vermelho quando meta.touched é true. Sem marcar
    // todos os campos aqui, um submit incompleto destacaria apenas os que o usuário
    // chegou a focar — os demais obrigatórios ficariam em branco, sem indicar o que
    // falta. focus+blur é o que registra touched no final-form.
    api.getRegisteredFields().forEach((field: string) => {
      api.focus(field);
      api.blur(field);
    });

    api.submit();
  };

  const footerButtons = (
    <>
      <Button type="text" onClick={onCancel} disabled={isSaving}>
        {t('common:cancel')}
      </Button>
      <Button type="primary" onClick={onSaveClick} loading={isSaving}>
        {isEditing ? t('faturamento:spe.saveEdit') : t('faturamento:spe.saveNew')}
      </Button>
    </>
  );

  return (
    <Modal
      title={
        isEditing ? t('faturamento:spe.editTitle') : t('faturamento:spe.newTitle')
      }
      visible={visible}
      width={720}
      footerButtons={footerButtons}
      onCancel={onCancel}
    >
      <ModalBody>
        <Form schema={schema} onSubmit={submit} initialValues={initialValues}>
          {(formProps: any) => {
            formApi.current = formProps.form;

            return (
              <>
                <Section>
                  <SectionTitle>
                    {t('faturamento:spe.sectionCompany')}
                  </SectionTitle>
                  <SectionHint>
                    {t('faturamento:spe.sectionCompanyHint')}
                  </SectionHint>

                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldCnpj')}
                        placeholder={req(t('faturamento:spe.fieldCnpj'))}
                        name="cnpj"
                        parse={(value: any) => stringToCNPJ(value)}
                        maxLenght={18}
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldCompanyName')}
                        placeholder={req(t('faturamento:spe.fieldCompanyName'))}
                        name="companyName"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        label={t('common:phone')}
                        placeholder={req(t('common:phone'))}
                        name="phone"
                        parse={(value: any) => stringToPhone(value)}
                        maxLenght={15}
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('common:email')}
                        placeholder={req(t('common:email'))}
                        name="email"
                        type="email"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldOwnerName')}
                        placeholder={req(t('faturamento:spe.fieldOwnerName'))}
                        name="ownerName"
                      />
                    </Col>
                    <Col>
                      {/* Site não é obrigatório: sem asterisco. */}
                      <Field label={t('common:site')} name="site" />
                    </Col>
                  </Row>
                </Section>

                <Section>
                  <SectionTitle>
                    {t('faturamento:spe.sectionAddress')}
                  </SectionTitle>

                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldZip')}
                        placeholder={req(t('faturamento:spe.fieldZip'))}
                        name="zip"
                        parse={(value: any) => stringToCEP(value)}
                        maxLenght={9}
                      />
                    </Col>
                    <Col $grow={2}>
                      {/* Endereço não é obrigatório: sem asterisco. */}
                      <Field
                        label={t('faturamento:spe.fieldStreetAddress')}
                        name="streetAddress"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldNumber')}
                        placeholder={req(t('faturamento:spe.fieldNumber'))}
                        name="number"
                        type="number"
                      />
                    </Col>
                    <Col>
                      {/* Complemento é opcional: muito endereço comercial não tem. */}
                      <Field
                        label={t('faturamento:spe.fieldComplement')}
                        name="complement"
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldDistrict')}
                        placeholder={req(t('faturamento:spe.fieldDistrict'))}
                        name="district"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col $grow={2}>
                      <Field
                        label={t('faturamento:spe.fieldCity')}
                        placeholder={req(t('faturamento:spe.fieldCity'))}
                        name="city"
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldState')}
                        placeholder={req(t('faturamento:spe.fieldState'))}
                        name="state"
                        maxLenght={2}
                      />
                    </Col>
                  </Row>
                </Section>

                <Section>
                  <SectionTitle>
                    {t('faturamento:spe.sectionRepresentative')}
                  </SectionTitle>
                  <SectionHint>
                    {t('faturamento:spe.sectionRepresentativeHint')}
                  </SectionHint>

                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldRepresentativeName')}
                        placeholder={req(
                          t('faturamento:spe.fieldRepresentativeName')
                        )}
                        name="legalRepresentative.name"
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldRepresentativeCpf')}
                        placeholder={req(
                          t('faturamento:spe.fieldRepresentativeCpf')
                        )}
                        name="legalRepresentative.cpf"
                        parse={(value: any) => stringToCPF(value)}
                        maxLenght={14}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        label={t('faturamento:spe.fieldRepresentativeRole')}
                        placeholder={req(
                          t('faturamento:spe.fieldRepresentativeRole')
                        )}
                        name="legalRepresentative.role"
                      />
                    </Col>
                    <Col>
                      <Field
                        label={t('common:phone')}
                        placeholder={req(t('common:phone'))}
                        name="legalRepresentative.phone"
                        parse={(value: any) => stringToPhone(value)}
                        maxLenght={15}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        label={t('common:email')}
                        placeholder={req(t('common:email'))}
                        name="legalRepresentative.email"
                        type="email"
                      />
                    </Col>
                  </Row>
                </Section>
              </>
            );
          }}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default SpeFormModal;
